/**
 * IP → country resolution for the sessions list.
 *
 * Fully OFFLINE — no third-party request, no API key. A vendored country-level
 * IPv4 dataset (`ipCountryData.ts`, originally from the MIT-licensed ip3country)
 * is decoded in the browser and binary-searched for the country code; the
 * browser-native `Intl.DisplayNames` turns that code into a localised country
 * name. Country-level accuracy is an estimate (good enough for "where am I
 * signed in").
 *
 * The dataset (~0.5 MB base64) is loaded lazily on first lookup (dynamic import
 * → its own chunk) so it never weighs down the main bundle, decoded once, then
 * reused. Results are memoised per IP.
 *
 * NB: we drive the dataset ourselves rather than importing the ip3country npm
 * package — that package only runs under Node (its data uses `Buffer` and its
 * `init()` clears `require.cache`), so it silently fails in the browser.
 */

/** Resolved geography for an IP. */
export type Geo = { country: string; countryCode: string };

/** Resolved results (match → Geo, no match → null). */
const cache = new Map<string, Geo | null>();
/** In-flight lookups, so concurrent rows sharing an IP await one resolution. */
const inflight = new Map<string, Promise<Geo | null>>();

/* ---------- dataset (lazy, offline) ---------- */

type CountryDb = { lookup: (ipv4: string) => string | null };

let dbPromise: Promise<CountryDb> | null = null;

const decodeBase64 = (b64: string): Uint8Array => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
};

/**
 * Decode the packed dataset into parallel range/code arrays. Format (from
 * ip3country): a `**`-terminated 2-char country-code table, then run-length
 * encoded IPv4 range boundaries each tagged with a country-code index.
 */
const buildDb = (data: Uint8Array): CountryDb => {
  const countryTable: string[] = [];
  const ipRanges: number[] = [];
  const countryCodes: string[] = [];

  let i = 0;
  while (i < data.length) {
    const c1 = data[i++];
    const c2 = data[i++];
    countryTable.push(String.fromCharCode(c1) + String.fromCharCode(c2));
    if (String.fromCharCode(c1) === '*') break;
  }

  let lastEndRange = 0;
  while (i < data.length) {
    let count = 0;
    const n1 = data[i++];
    if (n1 < 240) {
      count = n1;
    } else if (n1 === 242) {
      count = data[i++] | (data[i++] << 8);
    } else if (n1 === 243) {
      count = data[i++] | (data[i++] << 8) | (data[i++] << 16);
    }
    lastEndRange += count * 256;
    const cc = data[i++];
    ipRanges.push(lastEndRange);
    countryCodes.push(countryTable[cc]);
  }

  const search = (value: number): number => {
    let min = 0;
    let max = ipRanges.length - 1;
    while (min < max) {
      const mid = (min + max) >>> 1;
      if (ipRanges[mid] <= value) min = mid + 1;
      else max = mid;
    }
    return min;
  };

  const lookup = (ipv4: string): string | null => {
    const p = ipv4.split('.');
    if (p.length !== 4) return null;
    const n = Number(p[0]) * 2 ** 24 + (Number(p[1]) << 16) + (Number(p[2]) << 8) + Number(p[3]);
    const cc = countryCodes[search(n)];
    return !cc || cc === '--' ? null : cc;
  };

  return { lookup };
};

const getDb = (): Promise<CountryDb> => {
  if (!dbPromise) {
    dbPromise = import('./ipCountryData').then(({ IP_COUNTRY_DATA_B64 }) => buildDb(decodeBase64(IP_COUNTRY_DATA_B64)));
  }
  return dbPromise;
};

/* ---------- IP helpers ---------- */

/** Strict dotted-quad check — the dataset is IPv4-only, so IPv6/junk resolve to
 *  "unknown". */
const isIpv4 = (ip: string): boolean => {
  const parts = ip.split('.');
  if (parts.length !== 4) return false;
  return parts.every((p) => /^\d{1,3}$/.test(p) && Number(p) <= 255);
};

/** RFC1918 / loopback / link-local — not geo-locatable, skip the lookup. */
const isPrivateIp = (ip: string): boolean =>
  /^(?:10\.|127\.|192\.168\.|169\.254\.)/.test(ip) || /^172\.(?:1[6-9]|2\d|3[01])\./.test(ip);

/** An `X-Forwarded-For`-style value may carry a list; the client IP is first. */
const firstIp = (raw: string): string => raw.split(',')[0].trim();

/* ---------- country name ---------- */

let regionNames: Intl.DisplayNames | null | undefined;

/** ISO country code → localised country name (native, no dependency); falls
 *  back to the bare code if `Intl.DisplayNames` is unavailable or has no name. */
const countryName = (code: string): string => {
  const upper = code.toUpperCase();
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(undefined, { type: 'region' });
    } catch {
      regionNames = null;
    }
  }
  if (!regionNames) return upper;
  try {
    return regionNames.of(upper) ?? upper;
  } catch {
    return upper;
  }
};

/* ---------- public API ---------- */

/**
 * Resolve a country for an IP, or `null` when unknown / un-resolvable (IPv6,
 * private range, or not in the dataset). Cached and de-duplicated per IP. A
 * failure to load the dataset chunk is not cached, so a later mount can retry.
 */
export const lookupCountry = async (rawIp: string): Promise<Geo | null> => {
  const ip = firstIp(rawIp ?? '');
  if (!ip || !isIpv4(ip) || isPrivateIp(ip)) return null;

  const cached = cache.get(ip);
  if (cached !== undefined) return cached;

  const existing = inflight.get(ip);
  if (existing) return existing;

  const request = (async (): Promise<Geo | null> => {
    try {
      const db = await getDb();
      const code = db.lookup(ip);
      const geo: Geo | null = code ? { country: countryName(code), countryCode: code } : null;
      cache.set(ip, geo);
      return geo;
    } catch {
      return null; // dataset chunk failed to load — leave uncached for a retry
    } finally {
      inflight.delete(ip);
    }
  })();

  inflight.set(ip, request);
  return request;
};

/**
 * Two-letter ISO country code → flag emoji (regional-indicator code points).
 * Returns '' for anything that isn't a valid AA–ZZ code.
 */
export const flagEmoji = (countryCode: string): string => {
  const cc = (countryCode ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  const BASE = 0x1f1e6; // regional indicator 'A'
  return String.fromCodePoint(BASE + (cc.charCodeAt(0) - 65), BASE + (cc.charCodeAt(1) - 65));
};
